import Minus from "@/icons/Minus";
import Plus from "@/icons/Plus";
import React, { useState, useRef, useEffect } from "react";
import { Select, SelectItem } from '@/components/ui/AppSelect';
import SeatHoverPopup from "./SeatHoverPopup";
import SeatSelectionModal from "./SeatSelectionModal";
import SeatUnassignmentModal from "./SeatUnassignmentModal";

type Seat = {
    seat_number: string;
    uuid?: string;
    position: { x: number; y: number };
    category?: string;
    accessible?: boolean;
};

type Row = {
    position: { x: number; y: number };
    row_number: string;
    row_number_position?: string;
    seats: Seat[];
    uuid?: string;
};

type Zone = {
    name: string;
    code: string;
    position: { x: number; y: number };
    rows: Row[];
};

type Stage = {
    id: string;
    name: string;
    type: 'square' | 'circle' | 'polygon';
    position: { x: number; y: number };
    size: { width: number; height: number };
    rotation?: number;
    points?: { x: number; y: number }[]; // For polygon stages
    color?: string;
    strokeColor?: string;
    strokeWidth?: number;
    curveRadius?: number; // For rounded square stages
};

type Entrance = {
    id: string;
    name: string;
    type: 'square';
    position: { x: number; y: number };
    size: { width: number; height: number };
    rotation?: number;
    color?: string;
    strokeColor?: string;
    strokeWidth?: number;
};

type Category = { name: string; color: string };

type Plan = {
    name?: string;
    categories: Category[];
    zones: Zone[];
    stages?: Stage[];
    entrances?: Entrance[];
};

interface SeatStatusItem {
    type: 'reserved' | 'disabled';
    seat_uuid: string;
}

const PAN_SPEED = 1.5;
const MINIMAP_W = 160;
const MINIMAP_H = 100;

interface EventTicket {
    id: string;
    ticket_number: string;
    event_name: string;
    price: number;
    category: string;
    customer_name?: string;
    customer_email?: string;
    assigned_seat_uuid?: string;
}

interface Props {
    plan: Plan;
    data?: SeatStatusItem[];
    availableTickets?: EventTicket[];
    ticketAssignments?: { [seatUuid: string]: { ticketId: string; status: string } };
    ticketReservations?: { [seatUuid: string]: string };
    seatMapType?: 'client' | 'editor';
    onTicketAssignment?: (ticketId: string, seatUuids: string | string[]) => void;
    onTicketUnassignment?: (seatUuid: string) => void;
    onAssignedSeatClick?: (seatUuid: string, ticketId: string) => void;
    // Modal props
    isModalOpen?: boolean;
    onModalClose?: () => void;
    onModalSelect?: () => void;
    selectedSeatData?: {
        seatUuid: string;
        seatNumber: string;
        zone: string;
        row: string;
        seat: string;
    } | null;
    selectedTicketData?: {
        ticketId: string;
        ticketName: string;
        price: number;
        description?: string;
    } | null;
    eventData?: {
        title: string;
        date: string;
        doorsOpen: string;
        showStart: string;
        venue: string;
    };
    // Unassignment modal props
    isUnassignmentModalOpen?: boolean;
    onUnassignmentModalClose?: () => void;
    onUnassignmentModalConfirm?: () => void;
    unassignmentSeatData?: {
        seatUuid: string;
        seatNumber: string;
        zone: string;
        row: string;
        seat: string;
    } | null;
    unassignmentTicketData?: {
        ticketId: string;
        ticketName: string;
        price: number;
        description?: string;
    } | null;
    seatAssignmentsWithOrderInfo: any;
}

interface Transform {
    offsetX: number;
    offsetY: number;
    scale: number;
}

const SeatPlanSVG: React.FC<Props> = ({
    plan,
    data,
    availableTickets = [],
    ticketAssignments = {},
    ticketReservations = {},
    seatMapType = 'editor',
    onTicketAssignment,
    onTicketUnassignment,
    onAssignedSeatClick,
    isModalOpen = false,
    onModalClose,
    onModalSelect,
    selectedSeatData,
    selectedTicketData,
    eventData,
    isUnassignmentModalOpen = false,
    onUnassignmentModalClose,
    onUnassignmentModalConfirm,
    unassignmentSeatData,
    unassignmentTicketData,
    seatAssignmentsWithOrderInfo,
}) => {
    const svgRef = useRef<SVGSVGElement | null>(null);
    const [transform, setTransform] = useState<Transform>({
        offsetX: 0,
        offsetY: 0,
        scale: 1,
    });

    const [isPanning, setIsPanning] = useState(false);
    const panStart = useRef<{ x: number; y: number } | null>(null);
    const transformStart = useRef<Transform | null>(null);

    const [selectedSeats, setSelectedSeats] = useState<Array<{
        seat: Seat;
        row: Row;
        zone: Zone;
    }>>([]);

    const [selectedTicket, setSelectedTicket] = useState<EventTicket | null>(null);

    const [hoveredSeat, setHoveredSeat] = useState<{
        seat: Seat;
        row: Row;
        zone: Zone;
        ticketData?: {
            ticketId: string;
            ticketName: string;
            price: number;
            description?: string;
        } | null;
    } | null>(null);
    const [hoverPopupPosition, setHoverPopupPosition] = useState<{ x: number; y: number } | null>(null);

    // Multi-line polygon drawing state
    const [isDrawing, setIsDrawing] = useState(false);
    const [polygonPoints, setPolygonPoints] = useState<Array<{ x: number; y: number }>>([]);
    const [currentPoint, setCurrentPoint] = useState<{ x: number; y: number } | null>(null);
    const [isDrawingMode, setIsDrawingMode] = useState(false);
    const [highlightedZone, setHighlightedZone] = useState<string | null>(null);
    const [showHelp, setShowHelp] = useState(false);

    // Seat status types
    type SeatStatus = 'available' | 'reserved' | 'disabled' | 'selected' | 'assigned';

    // Determine seat status
    const getSeatStatus = (seat: Seat, row: Row, zone: Zone): SeatStatus => {
        // Check if seat has a ticket reservation (user selection)
        if (ticketReservations && ticketReservations[seat.uuid]) {
            if (seatMapType === 'client') {
                return 'selected'; // In client mode, show reservations as selected (blue)
            } else {
                return 'assigned'; // In editor mode, show as white/assigned but still selectable
            }
        }

        // Check if seat is in ticketAssignments (assigned to user's tickets)
        if (ticketAssignments && ticketAssignments[seat.uuid]) {
            if (seatMapType === 'client') {
                // In client mode, check if seat is reserved
                if (ticketAssignments[seat.uuid].status === 'reserved') {
                    return 'reserved'; // Show reserved seats as reserved
                }
                return 'assigned'; // In client mode, show assigned seats as assigned (with ticket colors)
            } else {
                // In editor mode, check if seat is reserved
                if (ticketAssignments[seat.uuid].status === 'reserved') {
                    return 'reserved'; // Show reserved seats as reserved
                }
                return 'assigned'; // In editor mode, show assigned seats as assigned (white)
            }
        }

        // Check if seat is selected
        const isSelected = selectedSeats.some(selected =>
            selected.seat.uuid === seat.uuid
        );

        if (isSelected) {
            return 'selected';
        }

        // Check if seat is reserved or disabled using data props
        const seatStatusItem = data?.find(item => item.seat_uuid === seat.uuid);
        if (seatStatusItem) {
            return seatStatusItem.type;
        }

        // In client mode, only show assigned seats as available, others as disabled
        if (seatMapType === 'client') {
            return 'disabled'; // All non-assigned seats are disabled in client mode
        }

        return 'available';
    };

    // Generate unique colors for different tickets with no duplicates
    const getTicketColor = (ticketId: string): { fill: string; stroke: string } => {
        // Get all unique ticket IDs from assignments
        const uniqueTicketIds = [...new Set(Object.values(ticketAssignments).map(assignment => assignment.ticketId))];

        // Predefined color palette
        const colors = [
            { fill: '#4CAF50', stroke: '#2E7D32' }, // Green
            { fill: '#FF9800', stroke: '#F57C00' }, // Orange
            { fill: '#2196F3', stroke: '#1976D2' }, // Blue
            { fill: '#FFEB3B', stroke: '#FBC02D' }, // Yellow
            { fill: '#9C27B0', stroke: '#7B1FA2' }, // Purple
        ];

        // Find the index of this ticket in the unique list
        const ticketIndex = uniqueTicketIds.indexOf(ticketId);

        // Assign color based on position in unique list
        const colorIndex = ticketIndex % colors.length;
        return colors[colorIndex];
    };

    // Check if seat is clickable
    const isSeatClickable = (seat: Seat): boolean => {
        // Check if seat is reserved or disabled using data props
        const seatStatusItem = data?.find(item => item.seat_uuid === seat.uuid);

        if (seatStatusItem) {
            return false; // Reserved or disabled seats are not clickable
        }

        // In client mode, only seats in ticketAssignments are clickable
        if (seatMapType === 'client') {
            const assignment = ticketAssignments && seat.uuid ? ticketAssignments[seat.uuid] : undefined;
            return !!assignment && !!assignment.ticketId && assignment.status !== 'reserved' && assignment.status !== 'hold';
        }

        // In editor mode, all seats are clickable
        return true;
    };

    // Clear all selected seats
    const clearAllSelections = () => {
        setSelectedSeats([]);
    };

    // Assign selected ticket to all selected seats
    const assignTicketToSelectedSeats = () => {
        if (selectedTicket && selectedSeats.length > 0) {
            const uuids = selectedSeats.map(selected => selected.seat.uuid);

            if (onTicketAssignment) {
                onTicketAssignment(selectedTicket.id, uuids);
            }

            setSelectedSeats([]);
            setSelectedTicket(null);
        }
    };


    // Select all available seats
    const selectAllAvailableSeats = () => {
        const allAvailableSeats: Array<{ seat: Seat; row: Row; zone: Zone }> = [];

        (plan.zones || []).forEach(zone => {
            (zone.rows || []).forEach(row => {
                row.seats.forEach(seat => {
                    if (isSeatClickable(seat)) {
                        allAvailableSeats.push({ seat, row, zone });
                    }
                });
            });
        });

        setSelectedSeats(allAvailableSeats);
    };

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.ctrlKey || e.metaKey) {
                switch (e.key) {
                    case 'a':
                        // Disable Ctrl+A in client mode
                        if (seatMapType === 'client') {
                            return;
                        }
                        e.preventDefault();
                        selectAllAvailableSeats();
                        break;
                    case 'Escape':
                        clearAllSelections();
                        break;
                }
            }

            // Enter key for bulk assignment
            if (e.key === 'Enter' && selectedSeats.length > 0 && selectedTicket) {
                e.preventDefault();
                assignTicketToSelectedSeats();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedSeats, selectedTicket]);

    // Cleanup animation frames on unmount
    useEffect(() => {
        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, []);

    // Zoom controls
    const zoomIn = () => {
        const newScale = Math.min(transform.scale * 1.5, 6.0);
        setTransform(prev => ({ ...prev, scale: newScale }));
    };

    const zoomOut = () => {
        const newScale = Math.max(transform.scale / 1.5, 0.1);
        setTransform(prev => ({ ...prev, scale: newScale }));
    };

    const resetZoom = () => {
        if (svgRef.current && boundingBox.maxX > 0) {
            const svgElement = svgRef.current;
            const containerWidth = svgElement.parentElement?.clientWidth || 1200;
            const contentWidth = boundingBox.maxX;
            const optimalScale = Math.min(containerWidth / contentWidth, 1.0);

            setTransform({
                offsetX: 0,
                offsetY: 0,
                scale: 1
            });
        }
    };


    const resetViewpoint = () => {
        setTransform(prev => ({ ...prev, ...getCenteredOffset(prev.scale) }));
    };

    const handleZoneClick = (zoneName: string) => {
        if (highlightedZone === zoneName) {
            setHighlightedZone(null); // Deselect if already selected
        } else {
            setHighlightedZone(zoneName); // Select new zone
        }
    };

    // Calculate bounding box for highlighted zone
    const getHighlightedZoneBounds = React.useMemo(() => {
        if (!highlightedZone) return null;

        const zone = plan.zones.find(z => z.name === highlightedZone);
        if (!zone) return null;

        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

        // Include zone position
        const zoneX = zone.position?.x ?? 0;
        const zoneY = zone.position?.y ?? 0;

        // Include all seats in the zone
        (zone.rows || []).forEach((row) => {
            const rowX = row.position?.x ?? 0;
            const rowY = row.position?.y ?? 0;
            (row.seats || []).forEach((seat) => {
                const seatX = seat.position?.x ?? 0;
                const seatY = seat.position?.y ?? 0;
                const absoluteX = zoneX + rowX + seatX;
                const absoluteY = zoneY + rowY + seatY;

                minX = Math.min(minX, absoluteX);
                minY = Math.min(minY, absoluteY);
                maxX = Math.max(maxX, absoluteX);
                maxY = Math.max(maxY, absoluteY);
            });
        });

        if (minX === Infinity) return null; // No seats found

        // Add padding around the bounding box
        const padding = 20;
        return {
            x: minX - padding,
            y: minY - padding,
            width: maxX - minX + (padding * 2),
            height: maxY - minY + (padding * 2)
        };
    }, [highlightedZone, plan]);

    // Compute bounding box for SVG size
    const boundingBox = React.useMemo(() => {
        let maxX = 0;
        let maxY = 0;

        // Include zones and seats
        (plan.zones || []).forEach((zone) => {
            const zx = zone.position?.x ?? 0;
            const zy = zone.position?.y ?? 0;
            (zone.rows || []).forEach((row) => {
                const rx = row.position?.x ?? 0;
                const ry = row.position?.y ?? 0;
                (row.seats || []).forEach((seat) => {
                    const sx = seat.position?.x ?? 0;
                    const sy = seat.position?.y ?? 0;
                    maxX = Math.max(maxX, zx + rx + sx + 20);
                    maxY = Math.max(maxY, zy + ry + sy + 20);
                });
            });
        });

        // Include stages
        (plan.stages || []).forEach((stage) => {
            maxX = Math.max(maxX, stage.position.x + stage.size.width + 20);
            maxY = Math.max(maxY, stage.position.y + stage.size.height + 20);
        });

        // Include entrances
        (plan.entrances || []).forEach((entrance) => {
            maxX = Math.max(maxX, entrance.position.x + entrance.size.width + 20);
            maxY = Math.max(maxY, entrance.position.y + entrance.size.height + 20);
        });

        return { maxX, maxY };
    }, [plan]);

    // Actual content bounds (min/max), used to center the plan within the SVG viewBox
    const contentBounds = React.useMemo(() => {
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        const consider = (x: number, y: number) => {
            minX = Math.min(minX, x);
            minY = Math.min(minY, y);
            maxX = Math.max(maxX, x);
            maxY = Math.max(maxY, y);
        };

        (plan.zones || []).forEach((zone) => {
            const zx = zone.position?.x ?? 0;
            const zy = zone.position?.y ?? 0;
            (zone.rows || []).forEach((row) => {
                const rx = row.position?.x ?? 0;
                const ry = row.position?.y ?? 0;
                (row.seats || []).forEach((seat) => {
                    const sx = seat.position?.x ?? 0;
                    const sy = seat.position?.y ?? 0;
                    consider(zx + rx + sx, zy + ry + sy);
                });
            });
        });

        (plan.stages || []).forEach((stage) => {
            consider(stage.position.x, stage.position.y);
            consider(stage.position.x + stage.size.width, stage.position.y + stage.size.height);
        });

        (plan.entrances || []).forEach((entrance) => {
            consider(entrance.position.x, entrance.position.y);
            consider(entrance.position.x + entrance.size.width, entrance.position.y + entrance.size.height);
        });

        if (minX === Infinity) return null;
        return { minX, minY, maxX, maxY };
    }, [plan]);

    const minimapSeats = React.useMemo(() => {
        const points: { x: number; y: number }[] = [];
        (plan.zones || []).forEach((zone) => {
            const zx = zone.position?.x ?? 0;
            const zy = zone.position?.y ?? 0;
            (zone.rows || []).forEach((row) => {
                const rx = row.position?.x ?? 0;
                const ry = row.position?.y ?? 0;
                (row.seats || []).forEach((seat) => {
                    points.push({ x: zx + rx + (seat.position?.x ?? 0), y: zy + ry + (seat.position?.y ?? 0) });
                });
            });
        });
        return points;
    }, [plan]);

    // Offset that centers the plan's actual content within the SVG viewBox
    const getCenteredOffset = React.useCallback((scale: number = 1) => {
        if (!contentBounds) return { offsetX: 0, offsetY: 0 };
        const viewBoxWidth = Math.max(1200, boundingBox.maxX);
        const viewBoxHeight = Math.max(600, boundingBox.maxY);
        const centerX = (contentBounds.minX + contentBounds.maxX) / 2;
        const centerY = (contentBounds.minY + contentBounds.maxY) / 2;
        return {
            offsetX: viewBoxWidth / 2 - centerX * scale,
            offsetY: viewBoxHeight / 2 - centerY * scale,
        };
    }, [contentBounds, boundingBox]);

    // Auto-scale to fit content width, and center the plan on first load / when it changes
    useEffect(() => {
        if (svgRef.current && boundingBox.maxX > 0) {
            const svgElement = svgRef.current;
            const containerWidth = svgElement.parentElement?.clientWidth || 1200;
            const contentWidth = boundingBox.maxX;
            const optimalScale = Math.min(containerWidth / contentWidth, 1.0);

            setTransform(() => ({
                scale: 1,
                ...getCenteredOffset(),
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [boundingBox.maxX, contentBounds]);

    // Handle click to detect seat
    const handleSeatClick = (seat: Seat, row: Row, zone: Zone, e: React.MouseEvent) => {
        // Only prevent if currently panning
        if (isPanning) {
            e.preventDefault();
            e.stopPropagation();
            return;
        }

        // Check if seat is clickable
        if (!isSeatClickable(seat)) {
            return;
        }

        // If we have a selected ticket, assign it to this seat
        if (seatMapType === 'client' && ticketAssignments[seat.uuid]) {
            if (onTicketAssignment) {
                onTicketAssignment(ticketAssignments[seat.uuid].ticketId, [seat.uuid]);
            }
            return;
        }
        if (selectedTicket && seatMapType === 'editor') {
            if (onTicketAssignment) {
                onTicketAssignment(selectedTicket.id, [seat.uuid]);
            }
            setSelectedTicket(null);
            return;
        }

        // Check if seat already has an assigned ticket (in editor mode)
        if (seatMapType === 'editor' && seat.uuid && ticketAssignments[seat.uuid]) {
            // Check if seat is reserved - if so, don't allow unassignment
            if (ticketAssignments[seat.uuid].status === 'reserved') {
                // Seat is reserved, don't allow unassignment
                return;
            }

            // This seat is assigned but not reserved - trigger unassignment modal via parent
            if (onAssignedSeatClick) {
                onAssignedSeatClick(seat.uuid, ticketAssignments[seat.uuid].ticketId);
            }
            return;
        }

        // Regular seat selection for assignment
        const isAlreadySelected = selectedSeats.some(selected =>
            selected.seat.uuid === seat.uuid
        );

        if (isAlreadySelected) {
            // Remove from selection
            setSelectedSeats(prev => prev.filter(selected =>
                selected.seat.uuid !== seat.uuid
            ));
        } else {
            // Add to selection
            setSelectedSeats(prev => [...prev, { seat, row, zone }]);
        }
    };

    // Handle touch to detect seat
    const handleSeatTouch = (seat: Seat, row: Row, zone: Zone, e: React.TouchEvent) => {
        e.preventDefault();
        e.stopPropagation();

        // Don't handle if currently panning
        if (isPanning) {
            return;
        }

        // Check if seat is clickable
        if (!isSeatClickable(seat)) {
            return;
        }

        // If we have a selected ticket, assign it to this seat
        if (seatMapType === 'client' && ticketAssignments[seat.uuid]) {
            if (onTicketAssignment) {
                onTicketAssignment(ticketAssignments[seat.uuid].ticketId, [seat.uuid]);
            }
            return;
        }
        if (selectedTicket && seatMapType === 'editor') {
            if (onTicketAssignment) {
                onTicketAssignment(selectedTicket.id, [seat.uuid]);
            }
            setSelectedTicket(null);
            return;
        }

        // Check if seat already has an assigned ticket (in editor mode)
        if (seatMapType === 'editor' && seat.uuid && ticketAssignments[seat.uuid]) {
            // Check if seat is reserved - if so, don't allow unassignment
            if (ticketAssignments[seat.uuid].status === 'reserved') {
                // Seat is reserved, don't allow unassignment
                return;
            }

            // This seat is assigned but not reserved - trigger unassignment modal via parent
            if (onAssignedSeatClick && seat.uuid) {
                onAssignedSeatClick(seat.uuid, ticketAssignments[seat.uuid].ticketId);
            }
            return;
        }

        // Regular seat selection for assignment
        const isAlreadySelected = selectedSeats.some(selected =>
            selected.seat.uuid === seat.uuid
        );

        if (isAlreadySelected) {
            // Remove from selection
            setSelectedSeats(prev => prev.filter(selected =>
                selected.seat.uuid !== seat.uuid
            ));
        } else {
            // Add to selection
            setSelectedSeats(prev => [...prev, { seat, row, zone }]);
        }
    };

    // Handle mouse enter for hover
    const handleSeatMouseEnter = (seat: Seat, row: Row, zone: Zone, event: React.MouseEvent) => {
        // Find ticket data for this seat
        let seatTicketData = null;
        if (seat.uuid && ticketAssignments && ticketAssignments[seat.uuid]) {
            const ticketId = ticketAssignments[seat.uuid].ticketId;
            const ticket = availableTickets?.find(t => t.id === ticketId);
            if (ticket) {
                seatTicketData = {
                    ticketId: ticket.id,
                    ticketName: ticket.ticket_number,
                    price: ticket.price,
                    description: ticket.event_name
                };
            }
        }

        setHoveredSeat({ seat, row, zone, ticketData: seatTicketData });
        setHoverPopupPosition({ x: event.clientX, y: event.clientY });
    };

    // Handle mouse leave for hover
    const handleSeatMouseLeave = () => {
        setHoveredSeat(null);
        setHoverPopupPosition(null);
    };

    // Handle row label click to select/deselect all seats in row
    const handleRowLabelClick = (row: Row, zone: Zone, e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        // Disable row label click selection in client mode
        if (seatMapType === 'client') {
            return;
        }

        const availableSeats = row.seats.filter(seat => isSeatClickable(seat));
        const newSelections = availableSeats.map(seat => ({ seat, row, zone }));

        // Check if all seats in this row are already selected
        const allSeatsSelected = availableSeats.every(seat =>
            selectedSeats.some(selected =>
                selected.seat.uuid === seat.uuid
            )
        );

        if (allSeatsSelected) {
            // Deselect all seats in this row
            setSelectedSeats(prev =>
                prev.filter(selected =>
                    !availableSeats.some(seat =>
                        selected.seat.uuid === seat.uuid
                    )
                )
            );
        } else {
            // Select all seats in this row (avoiding duplicates)
            setSelectedSeats(prev => {
                const existingSeats = new Set(
                    prev.map(selected => selected.seat.uuid)
                );
                const newSeats = newSelections.filter(selection =>
                    !existingSeats.has(selection.seat.uuid)
                );
                return [...prev, ...newSeats];
            });
        }
    };

    // Handle row label touch to select/deselect all seats in row
    const handleRowLabelTouch = (row: Row, zone: Zone, e: React.TouchEvent) => {
        e.preventDefault();
        e.stopPropagation();

        // Don't handle if currently panning
        if (isPanning) {
            return;
        }

        // Disable row label touch selection in client mode
        if (seatMapType === 'client') {
            return;
        }

        const availableSeats = row.seats.filter(seat => isSeatClickable(seat));
        const newSelections = availableSeats.map(seat => ({ seat, row, zone }));

        // Check if all seats in this row are already selected
        const allSeatsSelected = availableSeats.every(seat =>
            selectedSeats.some(selected =>
                selected.seat.uuid === seat.uuid
            )
        );

        if (allSeatsSelected) {
            // Deselect all seats in this row
            setSelectedSeats(prev =>
                prev.filter(selected =>
                    !availableSeats.some(seat =>
                        selected.seat.uuid === seat.uuid
                    )
                )
            );
        } else {
            // Select all seats in this row (avoiding duplicates)
            setSelectedSeats(prev => {
                const existingSeats = new Set(
                    prev.map(selected => selected.seat.uuid)
                );
                const newSeats = newSelections.filter(selection =>
                    !existingSeats.has(selection.seat.uuid)
                );
                return [...prev, ...newSeats];
            });
        }
    };

    // Cache for coordinate conversion optimization
    const coordinateCache = React.useRef<{
        rect: DOMRect | null;
        contentGroup: SVGGraphicsElement | null;
        transformMatrix: DOMMatrix | null;
        inverseMatrix: DOMMatrix | null;
        lastUpdate: number;
    }>({
        rect: null,
        contentGroup: null,
        transformMatrix: null,
        inverseMatrix: null,
        lastUpdate: 0
    });

    // Convert screen coordinates to SVG coordinates (optimized)
    const screenToSVG = React.useCallback((clientX: number, clientY: number) => {
        if (!svgRef.current) return { x: 0, y: 0 };

        const now = Date.now();
        const cache = coordinateCache.current;

        // Update cache if needed (every 100ms or if transform changed)
        if (now - cache.lastUpdate > 100 || !cache.rect || !cache.contentGroup) {
            cache.rect = svgRef.current.getBoundingClientRect();
            cache.contentGroup = svgRef.current.querySelector('g[transform]') as SVGGraphicsElement;
            cache.transformMatrix = cache.contentGroup?.getCTM() || null;
            cache.inverseMatrix = cache.transformMatrix?.inverse() || null;
            cache.lastUpdate = now;
        }

        if (!cache.rect || !cache.contentGroup || !cache.inverseMatrix) {
            return { x: 0, y: 0 };
        }

        // Create a point in screen coordinates relative to the SVG
        const point = svgRef.current.createSVGPoint();
        point.x = clientX - cache.rect.left;
        point.y = clientY - cache.rect.top;

        // Transform the point to SVG coordinates
        const transformedPoint = point.matrixTransform(cache.inverseMatrix);

        return { x: transformedPoint.x, y: transformedPoint.y };
    }, []);

    // Point-in-polygon algorithm (ray casting)
    const isPointInPolygon = (point: { x: number; y: number }, polygon: Array<{ x: number; y: number }>) => {
        if (polygon.length < 3) return false;

        let inside = false;
        for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
            if (((polygon[i].y > point.y) !== (polygon[j].y > point.y)) &&
                (point.x < (polygon[j].x - polygon[i].x) * (point.y - polygon[i].y) / (polygon[j].y - polygon[i].y) + polygon[i].x)) {
                inside = !inside;
            }
        }
        return inside;
    };

    // Check if a seat is within the polygon
    const isSeatInPolygon = (seat: Seat, row: Row, zone: Zone) => {
        if (polygonPoints.length < 3) return false;

        const seatX = (zone.position?.x ?? 0) + (row.position?.x ?? 0) + (seat.position?.x ?? 0);
        const seatY = (zone.position?.y ?? 0) + (row.position?.y ?? 0) + (seat.position?.y ?? 0);

        return isPointInPolygon({ x: seatX, y: seatY }, polygonPoints);
    };

    // Select seats within polygon
    const selectSeatsInPolygon = () => {
        if (polygonPoints.length < 3) return;

        const seatsInArea: Array<{ seat: Seat; row: Row; zone: Zone }> = [];

        (plan.zones || []).forEach(zone => {
            (zone.rows || []).forEach(row => {
                row.seats.forEach(seat => {
                    if (isSeatInPolygon(seat, row, zone) && isSeatClickable(seat)) {
                        seatsInArea.push({ seat, row, zone });
                    }
                });
            });
        });

        setSelectedSeats(prev => [...prev, ...seatsInArea]);
    };

    // Handle pan start
    const handleMouseDown = (e: React.MouseEvent<SVGSVGElement>) => {
        e.preventDefault();

        // Check if we're in drawing mode (Ctrl/Cmd key held)
        if (e.ctrlKey || e.metaKey) {
            // Disable polygon drawing in client mode
            if (seatMapType === 'client') {
                return;
            }
            const svgCoords = screenToSVG(e.clientX, e.clientY);

            if (!isDrawing) {
                // Start new polygon
                setIsDrawing(true);
                setIsDrawingMode(true);
                setPolygonPoints([svgCoords]);
                setCurrentPoint(svgCoords);
            } else {
                // Add point to existing polygon
                setPolygonPoints(prev => [...prev, svgCoords]);
                setCurrentPoint(svgCoords);
            }
            return;
        }

        setIsPanning(true);
        panStart.current = { x: e.clientX, y: e.clientY };
        transformStart.current = { ...transform };
    };

    // Optimized mouse move handler with requestAnimationFrame
    const animationFrameRef = React.useRef<number | null>(null);
    const lastMousePosition = React.useRef<{ x: number; y: number } | null>(null);

    const throttledMouseMove = React.useCallback(
        (e: React.MouseEvent<SVGSVGElement>) => {
            // Handle drawing mode
            if (isDrawing && isDrawingMode) {
                e.preventDefault();
                const svgCoords = screenToSVG(e.clientX, e.clientY);
                setCurrentPoint(svgCoords);
                return;
            }

            if (!isPanning || !panStart.current || !transformStart.current) {
                return;
            }

            e.preventDefault();

            // Store current mouse position
            lastMousePosition.current = { x: e.clientX, y: e.clientY };

            // Cancel previous animation frame if it exists
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }

            // Use requestAnimationFrame for smooth updates
            animationFrameRef.current = requestAnimationFrame(() => {
                if (!lastMousePosition.current || !panStart.current || !transformStart.current) {
                    return;
                }

                const dx = (lastMousePosition.current.x - panStart.current.x) * PAN_SPEED;
                const dy = (lastMousePosition.current.y - panStart.current.y) * PAN_SPEED;

                setTransform({
                    offsetX: transformStart.current.offsetX + dx,
                    offsetY: transformStart.current.offsetY + dy,
                    scale: transformStart.current.scale,
                });

                animationFrameRef.current = null;
            });
        },
        [isDrawing, isDrawingMode, isPanning, screenToSVG]
    );

    // Handle pan move
    const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
        throttledMouseMove(e);
    };

    // Handle pan end
    const handleMouseUp = (e: React.MouseEvent<SVGSVGElement>) => {
        if (isPanning) {
            e.preventDefault();
            setIsPanning(false);
            panStart.current = null;
            transformStart.current = null;

            // Cancel any pending animation frame
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }
        }
    };

    // Handle touch start
    const handleTouchStart = (e: React.TouchEvent<SVGSVGElement>) => {
        if (e.touches.length === 1) {
            const touch = e.touches[0];
            const target = e.target as Element;

            // Check if touching a seat or row label - if so, don't start panning
            if (target.closest('circle[data-seat]') || target.closest('text[data-row-label]')) {
                return; // Let the seat/row label handle the touch
            }

            // Only start panning if not touching interactive elements
            e.preventDefault();
            const rect = e.currentTarget.getBoundingClientRect();
            const x = touch.clientX - rect.left;
            const y = touch.clientY - rect.top;

            // Clear any pending animation frames
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }

            setIsPanning(true);
            panStart.current = { x, y };
            transformStart.current = { ...transform };
        }
    };

    // Handle touch move with throttling for smoother performance
    const handleTouchMove = (e: React.TouchEvent<SVGSVGElement>) => {
        if (isPanning && e.touches.length === 1 && panStart.current && transformStart.current) {
            e.preventDefault();

            const touch = e.touches[0];
            const rect = e.currentTarget.getBoundingClientRect();
            const x = touch.clientX - rect.left;
            const y = touch.clientY - rect.top;

            const dx = (x - panStart.current.x) * PAN_SPEED;
            const dy = (y - panStart.current.y) * PAN_SPEED;

            // Use requestAnimationFrame for smoother updates
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }

            animationFrameRef.current = requestAnimationFrame(() => {
                setTransform({
                    offsetX: transformStart.current!.offsetX + dx,
                    offsetY: transformStart.current!.offsetY + dy,
                    scale: transformStart.current!.scale,
                });
            });
        }
    };

    // Handle touch end
    const handleTouchEnd = (e: React.TouchEvent<SVGSVGElement>) => {
        e.preventDefault();
        if (isPanning) {
            setIsPanning(false);
            panStart.current = null;
            transformStart.current = null;
        }
    };

    // Handle double click to finish polygon
    const handleDoubleClick = (e: React.MouseEvent<SVGSVGElement>) => {
        // Disable polygon finishing in client mode
        if (seatMapType === 'client') {
            return;
        }

        if (isDrawing && isDrawingMode && polygonPoints.length >= 3) {
            e.preventDefault();
            selectSeatsInPolygon();
            setIsDrawing(false);
            setIsDrawingMode(false);
            setPolygonPoints([]);
            setCurrentPoint(null);
        }
    };

    // Handle right click to cancel polygon
    const handleContextMenu = (e: React.MouseEvent<SVGSVGElement>) => {
        // Disable polygon cancellation in client mode
        if (seatMapType === 'client') {
            return;
        }

        if (isDrawing && isDrawingMode) {
            e.preventDefault();
            setIsDrawing(false);
            setIsDrawingMode(false);
            setPolygonPoints([]);
            setCurrentPoint(null);
        }
    };

    // Build category map
    const categoryColors = new Map<string, string>();
    (plan.categories || []).forEach((cat) => categoryColors.set(cat.name, cat.color));

    const isClientMap = seatMapType === 'client';
    const legendTicketIds = [...new Set(Object.values(ticketAssignments).map(assignment => assignment.ticketId))];

    const viewBoxW = Math.max(1200, boundingBox.maxX);
    const viewBoxH = Math.max(600, boundingBox.maxY);
    const minimap = (() => {
        if (!contentBounds) return null;
        const pad = 20;
        const bx0 = contentBounds.minX - pad;
        const by0 = contentBounds.minY - pad;
        const bw = contentBounds.maxX - contentBounds.minX + pad * 2;
        const bh = contentBounds.maxY - contentBounds.minY + pad * 2;
        const m = Math.min(MINIMAP_W / bw, MINIMAP_H / bh);
        const ox = (MINIMAP_W - bw * m) / 2;
        const oy = (MINIMAP_H - bh * m) / 2;

        const svgW = svgRef.current?.clientWidth || viewBoxW;
        const svgH = svgRef.current?.clientHeight || viewBoxH;
        const fit = Math.min(svgW / viewBoxW, svgH / viewBoxH) || 1;
        const visW = svgW / fit;
        const visH = svgH / fit;
        const visX = (viewBoxW - visW) / 2;
        const visY = (viewBoxH - visH) / 2;
        const s0 = transform.scale || 1;

        return {
            toMini: (x: number, y: number) => ({ x: ox + (x - bx0) * m, y: oy + (y - by0) * m }),
            fromMini: (mx: number, my: number) => ({ x: bx0 + (mx - ox) / m, y: by0 + (my - oy) / m }),
            m,
            view: {
                x: ox + ((visX - transform.offsetX) / s0 - bx0) * m,
                y: oy + ((visY - transform.offsetY) / s0 - by0) * m,
                w: (visW / s0) * m,
                h: (visH / s0) * m,
            },
        };
    })();

    const moveViewToMinimapPoint = (e: React.PointerEvent<HTMLDivElement>) => {
        if (!minimap) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const point = minimap.fromMini(e.clientX - rect.left, e.clientY - rect.top);
        setTransform(prev => ({
            ...prev,
            offsetX: viewBoxW / 2 - point.x * prev.scale,
            offsetY: viewBoxH / 2 - point.y * prev.scale,
        }));
    };

    return (
        <div className={isClientMap ? "h-[440px] md:h-[600px]" : ""} style={{
            overflow: "auto",
            overflowY: "auto",
            boxShadow: isClientMap ? "none" : "0 2px 8px rgba(0, 0, 0, 0.15)",
            borderRadius: isClientMap ? "4px" : "8px",
            padding: "0px",
            userSelect: "none",
            width: "100%",
            height: isClientMap ? undefined : "calc(100vh - 190px)",
            maxHeight: isClientMap ? "100%" : undefined,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            position: "relative",
            touchAction: isClientMap ? "pan-y" : "none",
            WebkitOverflowScrolling: "touch",
            overscrollBehavior: "contain"
        }}>
            {/* Zoom Controls - Top Left of SVG */}
            <div
                className="bg-white dark:bg-nightElevated border border-gray-200 dark:border-nightBorder"
                style={{
                    position: "absolute",
                    top: "16px",
                    left: "16px",
                    zIndex: 1000,
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: "2px",
                    padding: "4px",
                    borderRadius: "10px",
                    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15)",
                }}
            >
                <button
                    onClick={zoomIn}
                    className="text-[#444] dark:text-nightTextSecondary/80 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                    style={{
                        borderRadius: "6px",
                        cursor: "pointer",
                        width: "32px",
                        height: "32px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                    title="Zoom In"
                >
                    <Plus />
                </button>
                <button
                    onClick={zoomOut}
                    className="text-[#444] dark:text-nightTextSecondary/80 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                    style={{
                        borderRadius: "6px",
                        cursor: "pointer",
                        width: "32px",
                        height: "32px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                    title="Zoom Out"
                >
                    <Minus />
                </button>
                <button
                    onClick={resetZoom}
                    className="text-[#444] dark:text-nightTextSecondary/80 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                    style={{
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "12px",
                        fontWeight: 700,
                        padding: "0 10px",
                        height: "32px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                    title="Reset Zoom"
                >
                    100%
                </button>

                <div className="bg-gray-200 dark:bg-nightBorder" style={{ width: "1px", height: "20px", margin: "0 2px" }} />

                <button
                    onClick={resetViewpoint}
                    className="text-[#444] dark:text-nightTextSecondary/80 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                    style={{
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "12px",
                        fontWeight: 700,
                        padding: "0 10px",
                        height: "32px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                    title="Reset Viewpoint to Center"
                >
                    Center
                </button>

                <div className="bg-gray-200 dark:bg-nightBorder" style={{ width: "1px", height: "20px", margin: "0 2px" }} />

                <button
                    onClick={() => setShowHelp(v => !v)}
                    className={showHelp ? "bg-primary40 text-white dark:bg-nightAccent dark:text-[#080808] rounded" : "text-[#444] dark:text-nightTextSecondary/80 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"}
                    style={{
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: 700,
                        width: "32px",
                        height: "32px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                    title="Seat map controls help"
                >
                    ?
                </button>

            </div>

            {/* Help Popover - toggled from the "?" button above */}
            {showHelp && (
                <div
                    className="absolute z-[1001] rounded shadow-lg border border-gray-200 dark:border-nightBorder bg-white dark:bg-nightElevated text-[#333] dark:text-nightTextSecondary"
                    style={{ top: "58px", left: "16px", width: "min(320px, calc(100% - 32px))", fontSize: "12px" }}
                >
                    <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 dark:border-nightBorder">
                        <span className="font-semibold text-[#222] dark:text-nightTextSecondary">Seat Map Controls</span>
                        <button
                            onClick={() => setShowHelp(false)}
                            className="text-gray-400 dark:text-nightMuted hover:text-gray-600 dark:hover:text-white"
                            aria-label="Close help"
                        >
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
                        </button>
                    </div>
                    <div className="p-3 flex flex-col gap-3 text-[#666] dark:text-nightMuted">
                        {seatMapType === 'editor' && (
                            <div>
                                <div className="font-semibold text-[#222] dark:text-nightTextSecondary mb-1">Multi-Select</div>
                                <div>Ctrl + click: draw polygon</div>
                                <div>Double-click: finish polygon</div>
                                <div>Right-click: cancel polygon</div>
                            </div>
                        )}
                        <div>
                            <div className="font-semibold text-[#222] dark:text-nightTextSecondary mb-1">Selection</div>
                            {seatMapType === 'editor' && (
                                <>
                                    <div>Click row labels: select entire row</div>
                                    <div>Ctrl + A: select all available seats</div>
                                </>
                            )}
                            <div>Click individual seats: toggle selection</div>
                        </div>
                    </div>
                </div>
            )}


            {minimap && !(selectedSeats.length > 0 && availableTickets.length > 0 && !isClientMap) && (
                <div
                    className="bg-white/95 dark:bg-nightElevated/95 border border-gray-200 dark:border-nightBorder cursor-pointer"
                    style={{
                        position: "absolute",
                        top: "16px",
                        right: "16px",
                        width: `${MINIMAP_W}px`,
                        height: `${MINIMAP_H}px`,
                        borderRadius: "4px",
                        overflow: "hidden",
                        zIndex: 10,
                        touchAction: "none",
                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                    }}
                    onPointerDown={(e) => {
                        e.stopPropagation();
                        e.currentTarget.setPointerCapture(e.pointerId);
                        moveViewToMinimapPoint(e);
                    }}
                    onPointerMove={(e) => {
                        if (e.currentTarget.hasPointerCapture(e.pointerId)) moveViewToMinimapPoint(e);
                    }}
                    title="Click or drag to move the view"
                >
                    <svg width={MINIMAP_W} height={MINIMAP_H} className="text-gray-400 dark:text-nightTextSecondary/40">
                        {(plan.stages || []).map((stage, i) => {
                            const p = minimap.toMini(stage.position.x, stage.position.y);
                            return (
                                <rect key={`st-${i}`} x={p.x} y={p.y} width={stage.size.width * minimap.m} height={stage.size.height * minimap.m} fill="currentColor" opacity={0.5} />
                            );
                        })}
                        {minimapSeats.map((pt, i) => {
                            const p = minimap.toMini(pt.x, pt.y);
                            return <rect key={i} x={p.x - 0.75} y={p.y - 0.75} width={1.5} height={1.5} fill="currentColor" />;
                        })}
                        <rect
                            x={minimap.view.x}
                            y={minimap.view.y}
                            width={Math.max(4, minimap.view.w)}
                            height={Math.max(4, minimap.view.h)}
                            fill="rgba(34, 201, 205, 0.15)"
                            stroke="#22C9CD"
                            strokeWidth={1.5}
                        />
                    </svg>
                </div>
            )}

            {/* Ticket Selection - Top Right, only when seats are selected */}
            {selectedSeats.length > 0 && availableTickets.length > 0 && (
                <div
                    className="bg-white dark:bg-nightElevated border border-gray-200 dark:border-nightBorder"
                    style={{
                        position: "absolute",
                        top: "16px",
                        right: "16px",
                        zIndex: 1000,
                        display: "flex",
                        flexDirection: "column",
                        gap: "16px",
                        padding: "20px",
                        borderRadius: "14px",
                        boxShadow: "0 8px 24px rgba(0, 0, 0, 0.25)",
                        width: "320px",
                        maxHeight: "calc(100% - 32px)",
                        overflowY: "auto",
                    }}
                >
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <span className="w-8 h-8 rounded bg-[#E6F6F4] dark:bg-nightAccent/10 text-primary40 dark:text-nightAccent flex items-center justify-center flex-shrink-0">
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M1.5 6.5V4.667A1.167 1.167 0 012.667 3.5h10.666A1.167 1.167 0 0114.5 4.667V6.5a1.167 1.167 0 000 2.333v1.834a1.167 1.167 0 01-1.167 1.166H2.667A1.167 1.167 0 011.5 10.667V8.833a1.167 1.167 0 000-2.333z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
                                    <path d="M6.5 3.5v9" stroke="currentColor" strokeWidth="1.3" strokeDasharray="1.5 1.5" />
                                </svg>
                            </span>
                            <span className="text-[16px] font-bold text-[#222] dark:text-nightTextSecondary">
                                Assign Ticket
                            </span>
                        </div>
                        <span className="text-[12px] font-semibold px-2.5 py-1 rounded-full bg-[#E6F6F4] dark:bg-nightAccent/10 text-primary40 dark:text-nightAccent flex-shrink-0">
                            {selectedSeats.length} seat{selectedSeats.length !== 1 ? 's' : ''}
                        </span>
                    </div>

                    {/* Selected seats */}
                    <div>
                        <div className="text-[12px] font-medium text-[#666] dark:text-nightMuted mb-2">
                            Selected seats
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            {selectedSeats.slice(0, 12).map((s, idx) => (
                                <span
                                    key={idx}
                                    className="bg-gray-100 dark:bg-white/10 text-[#222] dark:text-nightTextSecondary"
                                    style={{ padding: "3px 8px", borderRadius: "6px", fontSize: "12px", fontWeight: 600 }}
                                >
                                    {s.row.row_number}{s.seat.seat_number}
                                </span>
                            ))}
                            {selectedSeats.length > 12 && (
                                <span className="text-[12px] text-[#888] dark:text-nightMuted" style={{ padding: "3px 4px" }}>
                                    +{selectedSeats.length - 12} more
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Ticket Selection */}
                    <div>
                        <label className="block text-[12px] font-medium text-[#666] dark:text-nightMuted mb-1.5">
                            Select ticket
                        </label>
                        <Select
                            aria-label="Select ticket"
                            placeholder="Choose a ticket..."
                            selectedKeys={selectedTicket ? [selectedTicket.id] : []}
                            onSelectionChange={(keys) => {
                                const id = Array.from(keys)[0] as string | undefined;
                                const ticket = availableTickets.find(t => t.id === id);
                                setSelectedTicket(ticket || null);
                            }}
                            classNames={{
                                trigger: "bg-gray-50 dark:bg-nightSurface border border-gray-200 dark:border-nightBorder data-[hover=true]:bg-gray-50 dark:data-[hover=true]:bg-nightSurface rounded h-11",
                                value: "text-[#222] dark:text-nightTextSecondary text-[14px] font-medium",
                                popoverContent: "bg-white dark:bg-nightElevated border border-gray-200 dark:border-nightBorder",
                            }}
                        >
                            {availableTickets
                                //.filter(ticket => !ticket.assigned_seat_uuid)
                                .map(ticket => (
                                    <SelectItem key={ticket.id}>
                                        {ticket.ticket_number} (${ticket.price})
                                    </SelectItem>
                                ))}
                        </Select>
                        {selectedTicket && (
                            <div className="text-[12px] text-[#888] dark:text-nightMuted mt-2">
                                Click a seat on the map to assign it, or assign to all selected seats below.
                            </div>
                        )}
                    </div>

                    {/* Bulk Assignment Controls */}
                    {selectedTicket && (
                        <div className="bg-[#E6F6F4] dark:bg-nightAccent/10 rounded p-4">
                            <div className="text-[13px] font-semibold text-primary40 dark:text-nightAccent mb-3">
                                Assign "{selectedTicket.ticket_number}" to {selectedSeats.length} seat{selectedSeats.length !== 1 ? 's' : ''}?
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={assignTicketToSelectedSeats}
                                    className="flex-1 bg-primary40 text-white hover:opacity-90 transition-opacity dark:bg-nightAccent dark:text-[#080808] rounded"
                                    style={{
                                        padding: "10px 14px",
                                        border: "none",
                                        borderRadius: "8px",
                                        cursor: "pointer",
                                        fontSize: "13px",
                                        fontWeight: "bold"
                                    }}
                                    title="Assign ticket to all selected seats"
                                >
                                    Assign ({selectedSeats.length})
                                </button>
                                <button
                                    onClick={() => {
                                        setSelectedSeats([]);
                                        setSelectedTicket(null);
                                    }}
                                    className="bg-white dark:bg-white/10 text-[#222] dark:text-nightTextSecondary hover:bg-gray-100 dark:hover:bg-white/20 transition-colors"
                                    style={{
                                        padding: "10px 14px",
                                        border: "none",
                                        borderRadius: "8px",
                                        cursor: "pointer",
                                        fontSize: "13px",
                                        fontWeight: "bold"
                                    }}
                                    title="Cancel assignment"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Selection Controls */}
                    <button
                        onClick={clearAllSelections}
                        className="text-[#C62828] dark:text-red-400 border border-[#C62828]/30 dark:border-red-400/30 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                        style={{
                            padding: "10px 14px",
                            backgroundColor: "transparent",
                            borderRadius: "8px",
                            cursor: "pointer",
                            fontSize: "13px",
                            fontWeight: "bold"
                        }}
                        title="Clear All Selections"
                    >
                        Clear selection ({selectedSeats.length})
                    </button>
                </div>
            )}

            <svg
                ref={svgRef}
                width="100%"
                height="100%"
                viewBox={`0 0 ${Math.max(1200, boundingBox.maxX)} ${Math.max(600, boundingBox.maxY)}`}
                style={{
                    cursor: isDrawingMode ? "crosshair" : (hoveredSeat ? "pointer" : (isPanning ? "grabbing" : "grab")),
                    width: "100%",
                    height: isClientMap ? "100%" : "calc(90vh - 100px)",
                    flex: isClientMap ? "1 1 auto" : undefined,
                    minHeight: isClientMap ? 0 : undefined,
                    maxWidth: "100%",
                    maxHeight: isClientMap ? "none" : "calc(100vh - 100px)",
                    touchAction: "none",
                    userSelect: "none",
                    WebkitUserSelect: "none",
                    WebkitTouchCallout: "none"
                }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onDoubleClick={handleDoubleClick}
                onContextMenu={handleContextMenu}
            >
                <g transform={`translate(${transform.offsetX}, ${transform.offsetY}) scale(${transform.scale})`}>
                    {/* Polygon drawing overlay */}
                    {isDrawing && polygonPoints.length > 0 && (
                        <>
                            {/* Draw polygon lines */}
                            {polygonPoints.length > 1 && (
                                <polyline
                                    points={polygonPoints.map(p => `${p.x},${p.y}`).join(' ')}
                                    fill="none"
                                    stroke="#2196f3"
                                    strokeWidth="2"
                                    strokeDasharray="5,5"
                                    style={{ pointerEvents: "none" }}
                                />
                            )}

                            {/* Draw current line to mouse position */}
                            {currentPoint && polygonPoints.length > 0 && (
                                <line
                                    x1={polygonPoints[polygonPoints.length - 1].x}
                                    y1={polygonPoints[polygonPoints.length - 1].y}
                                    x2={currentPoint.x}
                                    y2={currentPoint.y}
                                    stroke="#2196f3"
                                    strokeWidth="2"
                                    strokeDasharray="3,3"
                                    style={{ pointerEvents: "none" }}
                                />
                            )}

                            {/* Draw polygon points */}
                            {polygonPoints.map((point, index) => (
                                <circle
                                    key={index}
                                    cx={point.x}
                                    cy={point.y}
                                    r="4"
                                    fill="#2196f3"
                                    stroke="#ffffff"
                                    strokeWidth="2"
                                    style={{ pointerEvents: "none" }}
                                />
                            ))}

                            {/* Draw current point */}
                            {currentPoint && (
                                <>
                                    <circle
                                        cx={currentPoint.x}
                                        cy={currentPoint.y}
                                        r="3"
                                        fill="rgba(33, 150, 243, 0.5)"
                                        stroke="#2196f3"
                                        strokeWidth="2"
                                        style={{ pointerEvents: "none" }}
                                    />
                                    {/* Debug text showing coordinates */}
                                    <text
                                        x={currentPoint.x + 10}
                                        y={currentPoint.y - 10}
                                        fontSize="10"
                                        fill="#2196f3"
                                        style={{ pointerEvents: "none" }}
                                    >
                                        ({Math.round(currentPoint.x)}, {Math.round(currentPoint.y)})
                                    </text>
                                </>
                            )}
                        </>
                    )}

                    {/* Highlighted Zone Bounding Box */}
                    {getHighlightedZoneBounds && (
                        <rect
                            x={getHighlightedZoneBounds.x}
                            y={getHighlightedZoneBounds.y}
                            width={getHighlightedZoneBounds.width}
                            height={getHighlightedZoneBounds.height}
                            fill="rgba(76, 175, 80, 0.1)"
                            stroke="#4caf50"
                            strokeWidth="3"
                            strokeDasharray="8,4"
                            style={{ pointerEvents: "none" }}
                        />
                    )}

                    {/* Render each zone */}
                    {(plan.zones || []).map((zone, zoneIndex) => {
                        const rows = zone.rows;
                        return (
                            <g key={zoneIndex}>
                                {/* Zone title */}
                                {/* <text
                                    x={zone.position?.x ?? 0}
                                    y={(zone.position?.y ?? 0) - 20}
                                    fontSize="16"
                                    fontWeight="bold"
                                    fill="#333"
                                    fontFamily="Arial, sans-serif"
                                >
                                    {zone.name} ({zone.code})
                                </text> */}

                                {/* Render each row in the zone */}
                                {(rows || []).map((row, rowIndex) => {
                                    const rowOffsetX = (zone.position?.x ?? 0) + (row.position?.x ?? 0);
                                    const rowOffsetY = (zone.position?.y ?? 0) + (row.position?.y ?? 0);

                                    // Calculate row angle from first two seats
                                    const firstSeat = row.seats[0];
                                    const secondSeat = row.seats[1];
                                    let angle = 0;
                                    if (firstSeat && secondSeat) {
                                        angle = Math.atan2(
                                            secondSeat.position.y - firstSeat.position.y,
                                            secondSeat.position.x - firstSeat.position.x
                                        );
                                    }

                                    return (
                                        <g key={rowIndex} className="row" transform={`translate(${rowOffsetX}, ${rowOffsetY})`}>
                                            {/* Row labels with proper angle calculation */}
                                            {(() => {
                                                // Calculate row angle based on first and second seat positions
                                                const firstSeat = row.seats[0];
                                                const secondSeat = row.seats[1];
                                                let angle = 0;

                                                if (firstSeat && secondSeat) {
                                                    angle = Math.atan2(
                                                        secondSeat.position.y - firstSeat.position.y,
                                                        secondSeat.position.x - firstSeat.position.x
                                                    );
                                                }

                                                const seatRadius = 10;
                                                const labelDistance = 25;

                                                // Use label positions from JSON data or calculate like PretixSeatMapEditor
                                                let leftLabelX, leftLabelY, rightLabelX, rightLabelY;
                                                const lastSeat = row.seats[row.seats.length - 1];

                                                // Parse row_number_position if available
                                                if (row.row_number_position) {
                                                    try {
                                                        const positionData = JSON.parse(row.row_number_position);
                                                        if (positionData.left) {
                                                            leftLabelX = positionData.left.x;
                                                            leftLabelY = positionData.left.y;
                                                        }
                                                        if (positionData.right) {
                                                            rightLabelX = positionData.right.x;
                                                            rightLabelY = positionData.right.y;
                                                        }
                                                    } catch (e) {
                                                        // Fallback to PretixSeatMapEditor calculation
                                                        const perpXLeft = -Math.cos(angle) * labelDistance;
                                                        const perpYLeft = -Math.sin(angle) * labelDistance;
                                                        leftLabelX = (firstSeat?.position.x ?? 0) + perpXLeft;
                                                        leftLabelY = (firstSeat?.position.y ?? 0) + perpYLeft;

                                                        const perpXRight = Math.cos(angle) * labelDistance;
                                                        const perpYRight = Math.sin(angle) * labelDistance;
                                                        rightLabelX = (lastSeat?.position.x ?? 0) + perpXRight;
                                                        rightLabelY = (lastSeat?.position.y ?? 0) + perpYRight;
                                                    }
                                                } else {
                                                    // Use PretixSeatMapEditor calculation
                                                    const perpXLeft = -Math.cos(angle) * labelDistance;
                                                    const perpYLeft = -Math.sin(angle) * labelDistance;
                                                    leftLabelX = (firstSeat?.position.x ?? 0) + perpXLeft;
                                                    leftLabelY = (firstSeat?.position.y ?? 0) + perpYLeft;

                                                    const perpXRight = Math.cos(angle) * labelDistance;
                                                    const perpYRight = Math.sin(angle) * labelDistance;
                                                    rightLabelX = (lastSeat?.position.x ?? 0) + perpXRight;
                                                    rightLabelY = (lastSeat?.position.y ?? 0) + perpYRight;
                                                }

                                                return (
                                                    <>
                                                        {/* Left side label */}
                                                        <text
                                                            x={leftLabelX}
                                                            y={leftLabelY}
                                                            dy=".3em"
                                                            fontSize="12px"
                                                            textAnchor="end"
                                                            fill="#888"
                                                            style={{ cursor: "pointer" }}
                                                            data-row-label="true"
                                                            onClick={(e) => handleRowLabelClick(row, zone, e)}
                                                            onTouchEnd={(e) => handleRowLabelTouch(row, zone, e)}
                                                            onMouseEnter={(e) => {
                                                                e.currentTarget.style.fill = "#2196f3";
                                                                e.currentTarget.style.fontWeight = "bold";
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                e.currentTarget.style.fill = "#888";
                                                                e.currentTarget.style.fontWeight = "normal";
                                                            }}
                                                        >
                                                            {row.row_number}
                                                        </text>

                                                        {/* Right side label */}
                                                        {lastSeat && (
                                                            <text
                                                                x={rightLabelX}
                                                                y={rightLabelY}
                                                                dy=".3em"
                                                                fontSize="12px"
                                                                textAnchor="start"
                                                                fill="#888"
                                                                style={{ cursor: "pointer" }}
                                                                onClick={(e) => handleRowLabelClick(row, zone, e)}
                                                                onTouchEnd={(e) => handleRowLabelTouch(row, zone, e)}
                                                                onMouseEnter={(e) => {
                                                                    e.currentTarget.style.fill = "#2196f3";
                                                                    e.currentTarget.style.fontWeight = "bold";
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    e.currentTarget.style.fill = "#888";
                                                                    e.currentTarget.style.fontWeight = "normal";
                                                                }}
                                                            >
                                                                {row.row_number}
                                                            </text>
                                                        )}
                                                    </>
                                                );
                                            })()}

                                            {/* Render each seat in the row */}
                                            {(row.seats || []).map((seat, seatIndex) => {
                                                const seatX = seat.position?.x ?? 0;
                                                const seatY = seat.position?.y ?? 0;

                                                const seatStatus = getSeatStatus(seat, row, zone);
                                                const isClickable = isSeatClickable(seat);

                                                const isHovered = hoveredSeat && (
                                                    hoveredSeat.seat.uuid === seat.uuid ||
                                                    hoveredSeat.seat === seat
                                                );

                                                const isInHighlightedZone = highlightedZone === zone.name;

                                                // Determine seat colors based on status
                                                let fillColor = "#ffffff";
                                                let strokeColor = "#000";
                                                let strokeWidth = "1";
                                                let cursor = "pointer";

                                                switch (seatStatus) {
                                                    case 'assigned':
                                                        // Use ticket colors in both editor and client mode
                                                        if (seat.uuid && ticketAssignments[seat.uuid]) {
                                                            const ticketColors = getTicketColor(ticketAssignments[seat.uuid].ticketId);
                                                            fillColor = ticketColors.fill;
                                                            strokeColor = ticketColors.stroke;
                                                        } else {
                                                            fillColor = "#4caf50";
                                                            strokeColor = "#2e7d32";
                                                        }
                                                        strokeWidth = "2";
                                                        // Assigned seats should be hoverable in both modes
                                                        cursor = "pointer";
                                                        break;
                                                    case 'selected':
                                                        // In client mode, use ticket color for selected seats
                                                        if (seatMapType === 'client') {
                                                            fillColor = "#22C9CD";
                                                            strokeColor = "#080808";
                                                        } else {
                                                            fillColor = "#ffcc00";
                                                            strokeColor = "#ff6600";
                                                        }
                                                        strokeWidth = "3";
                                                        break;
                                                    case 'reserved':
                                                        fillColor = seatMapType === 'client' ? "#D4D4D4" : "#ff4444";
                                                        strokeColor = seatMapType === 'client' ? "#A3A3A3" : "#cc0000";
                                                        strokeWidth = seatMapType === 'client' ? "1" : "3";
                                                        cursor = "not-allowed";
                                                        break;
                                                    case 'disabled':
                                                        fillColor = "#ddd";
                                                        strokeColor = "#999";
                                                        strokeWidth = "1";
                                                        cursor = "not-allowed";
                                                        break;
                                                    case 'available':
                                                    default:
                                                        // In client mode, use ticket color for available seats
                                                        if (seatMapType === 'client' && seat.uuid && ticketAssignments[seat.uuid]) {
                                                            const ticketColors = getTicketColor(ticketAssignments[seat.uuid].ticketId);
                                                            fillColor = ticketColors.fill;
                                                            strokeColor = ticketColors.stroke;
                                                            strokeWidth = "2";
                                                        } else if (isHovered) {
                                                            fillColor = "#e3f2fd";
                                                            strokeColor = "#2196f3";
                                                            strokeWidth = "2";
                                                        }
                                                        break;
                                                }

                                                // Apply zone highlighting if this seat is in the highlighted zone
                                                if (isInHighlightedZone && seatStatus === 'available') {
                                                    fillColor = "#e8f5e8";
                                                    strokeColor = "#4caf50";
                                                    strokeWidth = "2";
                                                }

                                                return (
                                                    <g key={seatIndex} className="seat">
                                                        {/* Seat circle */}
                                                        <circle
                                                            cx={seatX}
                                                            cy={seatY}
                                                            r="10"
                                                            fill={fillColor}
                                                            stroke={strokeColor}
                                                            strokeWidth={strokeWidth}
                                                            style={{ cursor }}
                                                            data-seat="true"
                                                            onClick={isClickable ? (e) => handleSeatClick(seat, row, zone, e) : undefined}
                                                            onTouchEnd={isClickable ? (e) => handleSeatTouch(seat, row, zone, e) : undefined}
                                                            onMouseEnter={(e) => {
                                                                // Allow hover for clickable seats or assigned seats in both modes
                                                                if (isClickable || (seat.uuid && ticketAssignments[seat.uuid])) {
                                                                    handleSeatMouseEnter(seat, row, zone, e);
                                                                }
                                                            }}
                                                            onMouseLeave={() => {
                                                                // Allow hover for clickable seats or assigned seats in both modes
                                                                if (isClickable || (seat.uuid && ticketAssignments[seat.uuid])) {
                                                                    handleSeatMouseLeave();
                                                                }
                                                            }}
                                                        />

                                                        {seat.accessible && (
                                                            <circle cx={seatX + 7.5} cy={seatY - 7.5} r={4} fill="#2563EB" stroke="#ffffff" strokeWidth={1} style={{ pointerEvents: "none" }} />
                                                        )}

                                                        {/* Seat number or check mark */}
                                                        {seatStatus === 'selected' && seatMapType === 'client' ? (
                                                            <text
                                                                x={seatX}
                                                                y={seatY}
                                                                fontSize="12px"
                                                                fill="#080808"
                                                                fontFamily="sans-serif"
                                                                textAnchor="middle"
                                                                dominantBaseline="middle"
                                                                style={{ pointerEvents: "none", fontWeight: "bold" }}
                                                            >
                                                                ✓
                                                            </text>
                                                        ) : (
                                                            <text
                                                                x={seatX}
                                                                y={seatY}
                                                                fontSize="10px"
                                                                fill={seatStatus === 'disabled' ? "#999" :
                                                                    (seatMapType === 'client' && seat.uuid && ticketAssignments[seat.uuid] ? "white" :
                                                                        (seatMapType === 'editor' && seatStatus === 'assigned' ? "white" : "#666666"))}
                                                                fontFamily="sans-serif"
                                                                textAnchor="middle"
                                                                dominantBaseline="middle"
                                                                style={{ pointerEvents: "none" }}
                                                            >
                                                                {seat.seat_number}
                                                            </text>
                                                        )}
                                                    </g>
                                                );
                                            })}
                                        </g>
                                    );
                                })}
                            </g>
                        )
                    })}

                    {/* Render Stages */}
                    {(plan.stages || []).map((stage, stageIndex) => (
                        <g key={`stage-${stageIndex}`} id={stage.id}>
                            <g transform={`translate(${stage.position.x}, ${stage.position.y}) rotate(${stage.rotation || 0} ${stage.size.width / 2} ${stage.size.height / 2})`}>
                                {stage.type === 'square' && (
                                    <rect
                                        x={0}
                                        y={0}
                                        width={stage.size.width}
                                        height={stage.size.height}
                                        fill={stage.color || '#FFD700'}
                                        stroke={stage.strokeColor || '#FFA500'}
                                        strokeWidth={stage.strokeWidth || 2}
                                        rx={stage.curveRadius || 0}
                                        ry={stage.curveRadius || 0}
                                    />
                                )}
                                {stage.type === 'circle' && (
                                    <ellipse
                                        cx={stage.size.width / 2}
                                        cy={stage.size.height / 2}
                                        rx={stage.size.width / 2}
                                        ry={stage.size.height / 2}
                                        fill={stage.color || '#FFD700'}
                                        stroke={stage.strokeColor || '#FFA500'}
                                        strokeWidth={stage.strokeWidth || 2}
                                    />
                                )}
                                {stage.type === 'polygon' && stage.points && (
                                    <polygon
                                        points={stage.points.map(p => `${p.x},${p.y}`).join(' ')}
                                        fill={stage.color || '#FFD700'}
                                        stroke={stage.strokeColor || '#FFA500'}
                                        strokeWidth={stage.strokeWidth || 2}
                                    />
                                )}

                                {/* Stage Label */}
                                <text
                                    x={stage.size.width / 2}
                                    y={stage.size.height / 2}
                                    fontSize="14"
                                    fontWeight="bold"
                                    fill="#000"
                                    fontFamily="Arial, sans-serif"
                                    textAnchor="middle"
                                    dominantBaseline="middle"
                                >
                                    {stage.name}
                                </text>
                            </g>
                        </g>
                    ))}

                    {/* Render Entrances */}
                    {(plan.entrances || []).map((entrance, entranceIndex) => (
                        <g key={`entrance-${entranceIndex}`} id={entrance.id}>
                            <g transform={`translate(${entrance.position.x}, ${entrance.position.y}) rotate(${entrance.rotation || 0} ${entrance.size.width / 2} ${entrance.size.height / 2})`}>
                                <rect
                                    x={0}
                                    y={0}
                                    width={entrance.size.width}
                                    height={entrance.size.height}
                                    fill={entrance.color || '#4CAF50'}
                                    stroke={entrance.strokeColor || '#2E7D32'}
                                    strokeWidth={entrance.strokeWidth || 2}
                                    strokeDasharray="5,5"
                                />

                                {/* Entrance Label */}
                                <text
                                    x={entrance.size.width / 2}
                                    y={entrance.size.height / 2}
                                    fontSize="12"
                                    fontWeight="bold"
                                    fill="#000"
                                    fontFamily="Arial, sans-serif"
                                    textAnchor="middle"
                                    dominantBaseline="middle"
                                >
                                    {entrance.name}
                                </text>
                            </g>
                        </g>
                    ))}
                </g>
            </svg>

            {isClientMap && (
                <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 px-4 py-3 bg-white dark:bg-nightElevated border-t border-gray-200 dark:border-nightBorder text-xs text-[#444] dark:text-nightTextSecondary/80">
                    {legendTicketIds.map(ticketId => {
                        const ticket = availableTickets.find(t => t.id === ticketId);
                        if (!ticket) return null;
                        return (
                            <span key={ticketId} className="flex items-center gap-1.5">
                                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: getTicketColor(ticketId).fill }} />
                                {ticket.category} · ${ticket.price}
                            </span>
                        );
                    })}
                    <span className="flex items-center gap-1.5">
                        <span className="w-3 h-3 rounded-full bg-[#22C9CD] ring-2 ring-[#080808] dark:ring-white/80" />
                        Selected
                    </span>
                    <span className="flex items-center gap-1.5">
                        <span className="w-3 h-3 rounded-full bg-[#D4D4D4]" />
                        Sold
                    </span>
                </div>
            )}

            {/* Zone Selector */}
            <div className="border border-gray-200 dark:border-nightBorder bg-white dark:bg-nightElevated" style={{
                marginTop: "10px",
                marginBottom: "10px",
                display: "flex",
                gap: "8px",
                padding: "8px 16px",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                flexWrap: "wrap",
                justifyContent: "center"
            }}>
                {(plan.zones || []).map((zone, index) => (
                    <button
                        key={index}
                        onClick={() => handleZoneClick(zone.name)}
                        className={highlightedZone === zone.name ? "" : "bg-gray-50 dark:bg-white/5 border-gray-200 dark:border-nightBorder text-[#333] dark:text-nightTextSecondary"}
                        style={{
                            padding: "4px 12px",
                            backgroundColor: highlightedZone === zone.name ? "#4caf50" : undefined,
                            color: highlightedZone === zone.name ? "#ffffff" : undefined,
                            border: highlightedZone === zone.name ? "1px solid #4caf50" : "1px solid",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "11px",
                            fontWeight: "bold",
                            transition: "all 0.2s ease"
                        }}
                        title={`Click to highlight ${zone.name} seats`}
                    >
                        {zone.name} ({zone.code})
                    </button>
                ))}
            </div>

            {/* Hover Popup - Show in client mode or editor mode for assigned tickets */}
            {hoveredSeat && hoverPopupPosition && (
                (seatMapType === 'client' && hoveredSeat.ticketData) ||
                (seatMapType === 'editor' && hoveredSeat.ticketData)
            ) && (
                    <SeatHoverPopup
                        seat={hoveredSeat.seat}
                        row={hoveredSeat.row}
                        zone={hoveredSeat.zone}
                        ticketData={hoveredSeat}
                        position={hoverPopupPosition}
                        ticketColor={hoveredSeat.ticketData?.ticketId ? getTicketColor(hoveredSeat.ticketData.ticketId) : undefined}
                    />
                )}

            {/* Seat Selection Modal - Inside SVG boundary */}
            {isModalOpen && selectedSeatData && selectedTicketData && (
                <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center z-50">
                    <div className="relative">
                        <SeatSelectionModal
                            isOpen={isModalOpen}
                            onClose={onModalClose || (() => { })}
                            onSelect={onModalSelect || (() => { })}
                            seatData={selectedSeatData}
                            ticketData={selectedTicketData}
                            eventData={eventData}
                            ticketColor={selectedTicketData?.ticketId ? getTicketColor(selectedTicketData.ticketId) : undefined}
                        />
                    </div>
                </div>
            )}

            {/* Seat Unassignment Modal - Inside SVG boundary */}
            {isUnassignmentModalOpen && unassignmentSeatData && unassignmentTicketData && (
                <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center z-50">
                    <div className="relative">
                        <SeatUnassignmentModal
                            isOpen={isUnassignmentModalOpen}
                            onClose={onUnassignmentModalClose || (() => { })}
                            onUnassign={onUnassignmentModalConfirm || (() => { })}
                            seatData={unassignmentSeatData}
                            ticketData={unassignmentTicketData}
                        />
                    </div>
                </div>
            )}


        </div>
    );
};

export default SeatPlanSVG;